import React from 'react';
import { Tag, Tooltip } from 'antd';
import { UserDeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

/**
 * Signalement des dossiers que personne ne suit.
 *
 * « Non affecte » se lit sur le redacteur : `SinistreResponse` le porte deja,
 * et la fiche l'affiche en toutes lettres dans l'onglet Journal. La regle vit
 * ici et nulle part ailleurs - le jour ou l'on voudra distinguer le dossier
 * confie mais pas encore pris en charge (ce que `StatutAffectation` sait deja
 * representer), il n'y aura qu'une ligne a reprendre.
 *
 * Les deux formes ne rendent RIEN quand le dossier est suivi, comme
 * `BadgeCanal` : marquer les milliers de dossiers normaux d'une pastille
 * « Affecte » noierait justement le signal qu'on cherche a faire ressortir.
 */
export const estNonAffecte = (sinistre) => !!sinistre && !sinistre.redacteurTrackingId;

const ROUGE = '#b91c1c';

/**
 * Banderole oblique pour l'en-tete de la fiche.
 *
 * Le decoupage est porte par le conteneur de la banderole, pas par l'en-tete
 * de page : celui-ci abrite les menus d'action, et un `overflow: hidden` a ce
 * niveau risquerait de les rogner.
 */
export function RubanNonAffecte({ sinistre }) {
  const { t } = useTranslation('dossierSinistre');
  if (!estNonAffecte(sinistre)) return null;

  return (
    <Tooltip title={t('nonAffecte.infobulle')}>
      <div style={S.cadre} aria-label={t('nonAffecte.dossierNonAffecte')}>
        <div style={S.ruban}>{t('nonAffecte.ruban')}</div>
      </div>
    </Tooltip>
  );
}

/** Pastille pour les lignes de la liste, ou la banderole ne tiendrait pas. */
export function PastilleNonAffecte({ sinistre, size = 'md' }) {
  const { t } = useTranslation('dossierSinistre');
  if (!estNonAffecte(sinistre)) return null;
  const petit = size === 'sm';

  return (
    <Tooltip title={t('nonAffecte.infobulle')}>
      <Tag
        color="red"
        icon={<UserDeleteOutlined />}
        style={{
          borderRadius: 14,
          fontSize: petit ? 10 : 11,
          fontWeight: 700,
          marginInlineEnd: 0,
        }}
      >
        {t('nonAffecte.pastille')}
      </Tag>
    </Tooltip>
  );
}

const S = {
  cadre: {
    position: 'absolute', top: 0, right: 0,
    width: 132, height: 132,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 2,
  },
  ruban: {
    position: 'absolute',
    // 34deg plutot que 45 : l'etiquette reste lisible d'un coup d'oeil.
    top: 26, right: -34,
    transform: 'rotate(34deg)',
    width: 180,
    textAlign: 'center',
    background: ROUGE,
    color: '#fff',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    padding: '4px 0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
    pointerEvents: 'auto',
  },
};
